import { useEffect, useMemo, useRef, useState } from "react";

export type CmdkProject = { id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  projects: CmdkProject[];
  onSelectProject: (id: string) => void;
};

function glyphChar(name: string): string {
  const letters = name.replace(/[^A-Za-z]/g, "");
  return (letters.slice(0, 1) || name.slice(0, 1) || "?").toUpperCase();
}

/**
 * ⌘K command palette — jump to a project. Purely a navigation convenience on
 * top of the existing project-select flow; carries no privileged data.
 */
export default function CommandPalette({ open, onClose, projects, onSelectProject }: Props) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const items = useMemo(() => {
    const f = q.trim().toLowerCase();
    return f
      ? projects.filter((p) => p.name.toLowerCase().includes(f) || p.id.toLowerCase().includes(f))
      : projects;
  }, [q, projects]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(items.length - 1, a + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const it = items[active];
        if (it) {
          onSelectProject(it.id);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, active, onClose, onSelectProject]);

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div className="cmdk" role="dialog" aria-label="Command palette" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3-3" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Jump to a project…"
            aria-label="Search projects"
          />
          <kbd>ESC</kbd>
        </div>
        <div className="cmdk-list">
          {items.length === 0 && <div className="cmdk-empty">No matches for “{q}”</div>}
          {items.length > 0 && <div className="cmdk-group">Projects</div>}
          {items.map((it, idx) => (
            <button
              key={it.id}
              type="button"
              className={`cmdk-item${idx === active ? " active" : ""}`}
              onMouseEnter={() => setActive(idx)}
              onClick={() => {
                onSelectProject(it.id);
                onClose();
              }}
            >
              <span className="glyph glyph-addon" aria-hidden="true">
                {glyphChar(it.name)}
              </span>
              <span className="cmdk-item-label">{it.name}</span>
              <span className="cmdk-item-sub">{it.id}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
